import React, { useEffect, useMemo } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { RouteComponentProps } from 'react-router'
import { createFilter } from 'react-select'
import { Field, FormikProps } from 'formik'
import { User } from '@graphics/gfx-common/dist/types'
import { IGroupEditorsState } from '../../types/editorsPerGroup'
import { ILanguages } from '../../types/languages'
import { IPackInput, FormObjectType, ISelectInput } from '../../types/pack'
import { IUserPreferences } from '../../types/userPreferences'
import { IGroupParam } from '../../types/router'
import { validateSlug } from '../../actions/pack'
import { getGroupEditors } from '../../actions/editorsPerGroup'
import { getUserPreferences } from '../../actions/userPreferences'
import { AppStateType } from '../../reducers'
import { selectGroupEditorsData } from '../../selectors/editorsPerGroup'
import { selectUserPreferences } from '../../selectors/userPreferences'
import { selectUserData } from '../../selectors/auth'
import {
  debouncedGetLocationsSuggestions,
  handleTopicsChange,
  handleLocationChange,
  getSluglineSuggestions,
  extendTopics,
  debouncedGetTopicsSuggestions,
  createDebouncedGetTagsSuggestions,
  topicsTypeaheadToFormAdapter,
} from '../../services/typeahead'
import {
  arePackFormValuesEmpty,
  createPackFormFieldsSetter,
  areCaseInsensitiveEqual,
  editorsStateToInputMapper,
  validateNewTag,
} from '../../services/packForm'
import { WrapContainer } from '../../layouts'
import {
  CustomTextField,
  CustomSelectField,
  TypeaheadSelect,
  CustomTypeheadSlugline,
} from '../../components'
import { PackPreviewFormFragment } from './PackPreviewFormFragment'
import { DefaultValueAdornment } from './DefaultValueAdornment'
import { validateTags, validateLocation } from './validation'

export interface IPackFormFragment {
  formikProps: FormikProps<IPackInput>
  languages: ILanguages
  isFormDisabled?: boolean
  validateImmediately?: boolean
  isLightGroup?: boolean
}

export interface IConnectedProps {
  dispatch: Dispatch
  groupEditorsData: IGroupEditorsState
  userPreferences: IUserPreferences
  user: User
}

export const PackFormFragmentComponent = ({
  match: {
    params: { groupId },
  },
  formikProps,
  languages,
  isFormDisabled,
  validateImmediately,
  dispatch,
  groupEditorsData: {
    items: groupEditors,
    isLoading: editorsLoading,
    isFailed: editorsFailed,
  },
  isLightGroup = false,
  userPreferences,
  user,
}: IPackFormFragment & IConnectedProps & RouteComponentProps<IGroupParam>) => {
  const { initialValues, setFieldValue } = formikProps
  useEffect(() => {
    if (!editorsLoading && !editorsFailed && !groupEditors) {
      dispatch(getGroupEditors(groupId))
    }
  }, [dispatch, groupId, editorsLoading, editorsFailed, groupEditors])

  useEffect(() => {
    if (!userPreferences.isLoading && !userPreferences.isLoaded) {
      dispatch(getUserPreferences())
    }
  }, [dispatch, userPreferences])

  const setPackFormFields = useMemo(() => (
    createPackFormFieldsSetter({ initialValues, setFieldValue })
  ), [initialValues, setFieldValue])

  const shouldSetDefaults =
    arePackFormValuesEmpty(formikProps.values) && userPreferences.isLoaded

  useEffect(() => {
    if (shouldSetDefaults) {
      setPackFormFields(userPreferences.byGroup[groupId], user)
    }
  }, [setPackFormFields, shouldSetDefaults, groupId, userPreferences, user])

  const sluglineChangeHandler = () => {
    formikProps.setFieldValue(
      'topicCodes',
      formikProps.values.topicCodes.filter(({ slugline }) => !slugline)
    )
  }

  const sluglineBlurHandler = ({ target: { value: slugValue } }) => {
    if (!!slugValue && !formikProps.errors.slugline) {
      formikProps.setStatus({ submitDisabled: true })
      dispatch(
        validateSlug(
          slugValue,
          formikProps.values.language,
          (value) => {
            formikProps.setFieldValue('slugline', value)
          },
          (suggestedTopics: FormObjectType[]) => {
            formikProps.setFieldValue(
              'topicCodes',
              extendTopics(formikProps.values.topicCodes, suggestedTopics)
            )
          },
          () => {
            formikProps.setStatus({ submitDisabled: false })
          }
        )
      )
    }
  }

  const createDefaultValueAdornment = (fieldName: string) => (
    <DefaultValueAdornment
      fieldName={fieldName}
      fieldValue={formikProps.values[fieldName]}
      fieldError={formikProps.errors[fieldName]}
      dispatch={dispatch}
      userGroupPreferences={userPreferences.byGroup[groupId]}
    />
  )

  return (
    <>
      <Field
        multiline
        name="title"
        label={isLightGroup ? 'Title' : 'Pack title'}
        component={CustomTextField}
        disabled={!!isFormDisabled}
        validateImmediately={validateImmediately}
        defaultValueAdornment={createDefaultValueAdornment('title')}
      />
      <Field
        multiline
        name="description"
        label={isLightGroup ? 'Caption' : 'Pack caption'}
        component={CustomTextField}
        disabled={!!isFormDisabled}
        validateImmediately={validateImmediately}
        defaultValueAdornment={createDefaultValueAdornment('description')}
      />
      <Field
        multiline
        name="byline"
        label="Byline"
        component={CustomTextField}
        disabled={!!isFormDisabled}
        validateImmediately={validateImmediately}
        defaultValueAdornment={createDefaultValueAdornment('byline')}
      />
      <WrapContainer direction="column" contains={3}>
        <Field
          name="language"
          label="Language"
          placeholder="Select pack language"
          component={CustomSelectField}
          disabled={!!isFormDisabled}
          validateImmediately={validateImmediately}
          defaultValueAdornment={createDefaultValueAdornment('language')}
        >
          {languages.list}
        </Field>
      </WrapContainer>
      <Field
        name="slugline"
        label="Slug"
        placeholder="Select or create slugline"
        helperText="WORD1-WORD2/"
        component={CustomTypeheadSlugline}
        loadOptions={getSluglineSuggestions}
        changeValue={(value) => value.toUpperCase()}
        onChange={sluglineChangeHandler}
        onBlur={sluglineBlurHandler}
        disabled={!!isFormDisabled}
        validateImmediately={validateImmediately}
        defaultValueAdornment={createDefaultValueAdornment('slugline')}
      />
      <WrapContainer direction="column" contains={3}>
        <Field
          name="location"
          label="Location"
          placeholder="Find your location"
          component={TypeaheadSelect}
          loadOptions={debouncedGetLocationsSuggestions}
          onChange={(value) => {
            formikProps.setFieldValue('location', value)
            formikProps.setStatus({ submitDisabled: true })
            handleLocationChange((extendedLocation) => {
              formikProps.setFieldValue('location', extendedLocation)
              formikProps.setStatus({ submitDisabled: false })
            })(value)
          }}
          disabled={!!isFormDisabled}
          isUppercase
          validateImmediately={validateImmediately}
          validate={validateLocation}
          defaultValueAdornment={createDefaultValueAdornment('location')}
        />
      </WrapContainer>
      <Field
        name="topicCodes"
        label="Topics"
        placeholder="Find topics"
        component={TypeaheadSelect}
        loadOptions={debouncedGetTopicsSuggestions}
        onChange={(value) => {
          formikProps.setFieldValue(
            'topicCodes',
            (value || []).map(topicsTypeaheadToFormAdapter)
          )
          formikProps.setStatus({ submitDisabled: true })
          handleTopicsChange((topics) => {
            formikProps.setFieldValue('topicCodes', topics)
            formikProps.setStatus({ submitDisabled: false })
          })(value)
        }}
        isMulti
        disabled={!!isFormDisabled}
        validateImmediately={validateImmediately}
        defaultValueAdornment={createDefaultValueAdornment('topicCodes')}
      />
      <Field
        name="contacts"
        label="Contact"
        placeholder="Find editors"
        component={TypeaheadSelect}
        options={
          groupEditors ? groupEditors.map(editorsStateToInputMapper) : []
        }
        onChange={(contacts: ISelectInput[]) => {
          formikProps.setFieldValue('contacts', contacts || [])
          if (!formikProps.touched.contacts) {
            formikProps.setFieldTouched('contacts')
          }
        }}
        noAsync
        isMulti
        disabled={!!isFormDisabled}
        filterOption={createFilter({ matchFrom: 'start' })}
        defaultValueAdornment={createDefaultValueAdornment('contacts')}
      />
      <Field
        name="tags"
        label="Tags"
        placeholder="Find tags"
        component={TypeaheadSelect}
        loadOptions={createDebouncedGetTagsSuggestions(groupId)}
        noOptionsMessage={({ inputValue }) =>
          formikProps.values.tags.some(({ label }) =>
            areCaseInsensitiveEqual(label)(inputValue)
          )
            ? 'The tag already exists in the list'
            : 'No options'
        }
        isCreatable={true}
        isMulti
        validate={validateTags}
        disabled={!!isFormDisabled}
        maxlength={50}
        defaultValueAdornment={createDefaultValueAdornment('tags')}
        isValidNewOption={validateNewTag}
      />
      <PackPreviewFormFragment
        formikProps={formikProps}
        isFormDisabled={isFormDisabled}
        isLightGroup={isLightGroup}
      />
    </>
  )
}

const mapStateToProps = (
  state: AppStateType,
  {
    match: {
      params: { groupId },
    },
  }: RouteComponentProps<IGroupParam>
) => ({
  ...selectUserData(state),
  groupEditorsData: selectGroupEditorsData(state, groupId),
  userPreferences: selectUserPreferences(state),
})

export const PackFormFragment = withRouter(
// @ts-ignore
connect(mapStateToProps)(PackFormFragmentComponent)
) as React.FC<IPackFormFragment>
